import { ArrowSquareOutIcon, PenNibIcon, WarningIcon } from '@phosphor-icons/react';
import { SketchPreview } from '~/features/sketches/sketch-preview';
import { AppDownloadMenu } from '~/features/apps/app-download-menu';
import { CanvasOpenButton } from '~/features/canvas/canvas-open-button';
import { useCanvasOpenHandler } from '~/features/canvas/canvas-link';

export interface SketchOutput {
  ok?: boolean;
  error?: string;
  sketch?: {
    slug?: string;
    title?: string;
    path?: string;
    url?: string;
    updated?: boolean;
    updatedAt?: string;
  };
}

/**
 * Specialised render for `sketch_show` (FRONT-1338). A sketch is a living
 * HTML document with a stable URL that updates in place as the user
 * iterates — so the preview always shows the CURRENT document (same
 * philosophy as AppPublishedCard's live status re-check), with the tool
 * output's `updatedAt` as the cache buster so an in-chat update reloads
 * the newest card's iframe.
 *
 * FRONT-2025: sketches moved onto the Apps store — the tool output now
 * carries `slug` (mirroring `DeckOutput`, `chat/deck-card.tsx`) instead of
 * the old UUID `id`, and `path` is `/apps/<slug>/`.
 *
 * The iframe is sandboxed with `allow-scripts` only — deliberately no
 * `allow-same-origin`: the document is model-authored, needs no cookies,
 * no SDK, and no same-origin API access (matches the admin draft-preview
 * precedent in app-preview.tsx; the served page carries its own strict
 * CSP as well).
 */
export function SketchCard({ output }: { output: SketchOutput }) {
  // Virtual desktop viewport scaled down to a thumbnail — see
  // `~/features/sketches/sketch-preview.tsx` (FRONT-1501) for the technique,
  // shared with the Sketches gallery.
  const width = 260;
  const height = 163;

  const sketch = output.sketch;
  const slug = sketch?.slug;
  const path = sketch?.path ?? (slug ? `/apps/${slug}/` : undefined);
  const cacheKey = sketch?.updatedAt ?? slug ?? '';
  const title = sketch?.title ?? 'Sketch';
  // FRONT-2744: a plain click opens the Canvas when it holds this sketch; a
  // modifier click keeps the real href's new-tab behaviour. Called before the
  // early return (Rules of Hooks).
  const openInCanvas = useCanvasOpenHandler(`sketch:${slug ?? ''}`);

  if (output.error || !path || !slug) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 border border-border rounded-lg">
        <WarningIcon size={14} />
        <span>{output.error ?? 'Sketch failed.'}</span>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <PenNibIcon size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium truncate text-foreground">{title}</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-muted text-muted-foreground border border-border px-1.5 py-0.5">
          {sketch?.updated ? 'Updated' : 'Sketch'}
        </span>
        {/* FRONT-1809/FRONT-2024: same shared Download control the Slides
            chat card uses — keeps the card compact, shares the trigger's
            own `icon-xs` size, no extra chrome. */}
        <AppDownloadMenu
          path={path}
          title={title}
          fallbackWord="sketch"
          testIdPrefix={`chat-sketch-download-${slug}`}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        />
        {/* FRONT-2594: renders nothing outside a Canvas context. */}
        <CanvasOpenButton itemKey={`sketch:${slug}`} />
      </div>
      <div className="p-3 flex gap-3 items-start">
        <SketchPreview
          path={path}
          title={sketch?.title ?? ''}
          cacheKey={String(cacheKey)}
          width={width}
          height={height}
          onOpen={openInCanvas}
        />
        <div className="flex-1 min-w-0 text-xs">
          <p className="mb-1.5 text-muted-foreground">
            {sketch?.updated
              ? 'Updated in place — same link, new content.'
              : 'A living document — ask EDDY to change it and the link stays.'}
          </p>
          <a
            href={path}
            target="_blank"
            rel="noreferrer noopener"
            onClick={openInCanvas}
            className="inline-flex items-center gap-1 py-1.5 -my-1.5 font-mono text-foreground hover:text-primary hover:underline break-all"
          >
            {sketch?.url ?? path}
            {!openInCanvas && <ArrowSquareOutIcon size={11} className="shrink-0" />}
          </a>
        </div>
      </div>
    </div>
  );
}
